import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../contexts/I18nContext';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import { filesAPI } from '../../services/api';
import { 
  Search, Filter, FileUp, Download, Eye, Clock, 
  CheckCircle, AlertCircle, XCircle, RefreshCw
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const FilesListPage = () => {
  const { t, locale } = useI18n();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const response = await filesAPI.list(params);
      setFiles(response.data);
    } catch (error) {
      console.error('Failed to fetch files:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles();
    // Poll for updates every 30 seconds
    const interval = setInterval(fetchFiles, 30000);
    return () => clearInterval(interval);
  }, [statusFilter]);

  const statusConfig = {
    pending: { icon: Clock, color: 'text-amber-500', bg: 'bg-amber-500/10', label: t('files.pending') },
    in_progress: { icon: RefreshCw, color: 'text-blue-500', bg: 'bg-blue-500/10', label: t('files.inProgress') },
    completed: { icon: CheckCircle, color: 'text-emerald-500', bg: 'bg-emerald-500/10', label: t('files.completed') },
    revision_requested: { icon: AlertCircle, color: 'text-orange-500', bg: 'bg-orange-500/10', label: t('files.revisionRequested') },
    cancelled: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-500/10', label: t('files.cancelled') },
  };

  const filteredFiles = files.filter(file => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      file.ticket_id.toLowerCase().includes(query) ||
      file.vehicle_brand.toLowerCase().includes(query) ||
      file.vehicle_model.toLowerCase().includes(query)
    );
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(amount);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="files-list-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-white">{t('files.title')}</h1>
            <p className="text-slate-400 mt-1">Verwalten Sie Ihre hochgeladenen Dateien</p>
          </div>
          <Link to="/upload">
            <Button className="btn-primary" data-testid="upload-new-btn">
              <FileUp className="w-4 h-4 mr-2" />
              {t('files.uploadNew')}
            </Button>
          </Link>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Suche nach Ticket-ID, Fahrzeug..."
              className="input-field pl-10"
              data-testid="search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="input-field w-full sm:w-48" data-testid="status-filter">
              <Filter className="w-4 h-4 mr-2 text-slate-500" />
              <SelectValue placeholder="Status filtern" />
            </SelectTrigger>
            <SelectContent className="bg-[#23272F] border-white/10">
              <SelectItem value="all">Alle Status</SelectItem>
              <SelectItem value="pending">{t('files.pending')}</SelectItem>
              <SelectItem value="in_progress">{t('files.inProgress')}</SelectItem>
              <SelectItem value="completed">{t('files.completed')}</SelectItem>
              <SelectItem value="revision_requested">{t('files.revisionRequested')}</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            onClick={fetchFiles}
            className="btn-secondary"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Files Table */}
        <div className="glass-card overflow-hidden">
          {loading && files.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-2 border-[#EC2229] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredFiles.length === 0 ? (
            <div className="text-center py-16">
              <FileUp className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">{t('files.noFiles')}</p>
              <Link to="/upload">
                <Button className="btn-primary mt-4">
                  <FileUp className="w-4 h-4 mr-2" />
                  Erste Datei hochladen
                </Button>
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">
                      {t('files.ticketId')}
                    </th>
                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">
                      {t('files.vehicle')}
                    </th>
                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">
                      {t('files.status')}
                    </th>
                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">
                      {t('upload.price')}
                    </th>
                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">
                      {t('files.date')}
                    </th>
                    <th className="text-right p-4 text-xs font-bold uppercase tracking-wider text-slate-500">
                      {t('files.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFiles.map((file) => {
                    const status = statusConfig[file.status] || statusConfig.pending;
                    const StatusIcon = status.icon;
                    
                    return (
                      <tr 
                        key={file.id} 
                        className="border-b border-white/5 hover:bg-white/5 transition-colors"
                        data-testid={`file-row-${file.ticket_id}`}
                      >
                        <td className="p-4">
                          <Link 
                            to={`/files/${file.id}`}
                            className="font-mono text-[#EC2229] hover:underline"
                          >
                            {file.ticket_id}
                          </Link>
                        </td>
                        <td className="p-4">
                          <div>
                            <p className="text-white font-medium">
                              {file.vehicle_brand} {file.vehicle_model}
                            </p>
                            <p className="text-sm text-slate-500">{file.vehicle_year}</p>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${status.bg}`}>
                            <StatusIcon className={`w-4 h-4 ${status.color}`} />
                            <span className={`text-sm font-medium ${status.color}`}>
                              {status.label}
                            </span>
                          </div>
                          {file.queue_position && file.status === 'pending' && (
                            <p className="text-xs text-slate-500 mt-1">
                              Position #{file.queue_position}
                            </p>
                          )}
                        </td>
                        <td className="p-4">
                          <span className="font-mono text-white">
                            {(file.credit_cost || 0).toFixed(1)} Cr
                          </span>
                        </td>
                        <td className="p-4">
                          <span className="text-sm text-slate-400">
                            {formatDate(file.created_at)}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center justify-end gap-2">
                            <Link to={`/files/${file.id}`}>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="btn-ghost"
                                data-testid={`view-${file.ticket_id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </Link>
                            {file.status === 'completed' && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="btn-ghost text-emerald-500"
                                onClick={async () => {
                                  try {
                                    const response = await filesAPI.download(file.id, 'tuned');
                                    const url = window.URL.createObjectURL(new Blob([response.data]));
                                    const link = document.createElement('a');
                                    link.href = url;
                                    link.download = `${file.ticket_id}_tuned.bin`;
                                    link.click();
                                  } catch (err) {
                                    console.error('Download failed:', err);
                                  }
                                }}
                                data-testid={`download-${file.ticket_id}`}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FilesListPage;
